import { Component } from "@theme/component";
import { onAnimationEnd, preloadImage } from "@theme/utilities";
import { ThemeEvents, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph-dom";
import { Cart } from "@theme/cart";

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest(".quick-add-modal")) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.add("atc-added");
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove("atc-added");
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class AddToCartFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(
      ".shopify-section, dialog, product-card-component",
    );
    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdateNextEventLoop,
      {
        signal,
      },
    );
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    const target = this.closest(
      ".shopify-section, dialog, product-card-component",
    );
    target?.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdateNextEventLoop,
    );
    target?.removeEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
    );
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behavior from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute(
        "disabled",
      ) === "true"
    ) {
      return;
    }

    const skipCartDrawer = typeof this.dataset.skipCartDrawer !== "undefined";

    if (skipCartDrawer && window.upcartSubscribeCartUpdated) {
      window.upcartOpenOnAddToCartOverride = false;
      window.upcartShouldSkipAddToCartInterceptor = true;

      window.addEventListener(
        "aftersell-upcart:public-events:cart-updated",
        () => {
          setTimeout(() => {
            window.upcartOpenOnAddToCartOverride = true;
            window.upcartShouldSkipAddToCartInterceptor = false;
          }, 1000);
        },
        { once: true },
      );
    }

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);

    // Scope by primary product, not by ancestor. `#product-buy-box` is a
    // merchant-editable anchor id, and the sticky footer lives in another
    // section, so an ancestor lookup would miss it. Product cards and
    // quick-add can still share that product id (recently viewed, related,
    // the card for this PDP), so skip those surfaces. The sticky footer is
    // none of them and must keep collecting the checked PDP accessories.
    const primaryProductId = String(
      formData.get("product-id") || this.dataset.productId || "",
    );
    const isCardOrQuickAddSurface = Boolean(
      this.closest(
        "product-card-component, quick-add-component, quick-add-dialog",
      ),
    );
    const checkedAccessoryInputs =
      primaryProductId && !isCardOrQuickAddSurface
        ? Array.from(
            document.querySelectorAll(
              `buy-box-pairs-well-component[data-primary-product-id="${primaryProductId}"] input[data-add-with-product]:checked`,
            ),
          )
        : [];

    const addToCartOptions = {
      productId: formData.get("product-id") || this.dataset.productId,
      productHandle:
        formData.get("product-handle") || this.dataset.productHandle,
      variantId: formData.get("id"),
      hasCrossSellRedirect:
        formData.get("product-cross-sell-redirect") === "true",
    };

    const cartCallbacks = {
      errorCallback: (response) => {
        if (skipCartDrawer && window.upcartSubscribeCartUpdated) {
          window.upcartOpenOnAddToCartOverride = true;
          window.upcartShouldSkipAddToCartInterceptor = false;
        }
        if (!addToCartTextError) return;
        addToCartTextError.classList.remove("hidden");

        // Reuse the text node if the user is spam-clicking
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = response.message;
        } else {
          const newTextNode = document.createTextNode(response.message);
          addToCartTextError.appendChild(newTextNode);
        }

        // Create or get existing error live region for screen readers
        this.#setLiveRegionText(response.message);

        this.#timeout = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add("hidden");

          // Clear the announcement
          this.#clearLiveRegionText();
        }, 10000);
      },
      successCallback: () => {
        if (addToCartTextError) {
          addToCartTextError.classList.add("hidden");
          addToCartTextError.removeAttribute("aria-live");
        }

        // Add aria-live region to inform screen readers that the item was added
        if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
          const addToCartButton =
            this.refs.addToCartButtonContainer.refs.addToCartButton;
          const addedTextElement = addToCartButton.querySelector(
            ".add-to-cart-text--added",
          );
          const addedText =
            addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, 5000);
        }
      },
    };

    if (checkedAccessoryInputs.length) {
      const primaryItem = {
        id: formData.get("id"),
        quantity: Number(formData.get("quantity") || 1),
      };

      const sellingPlan = formData.get("selling_plan");
      if (sellingPlan) {
        primaryItem.selling_plan = sellingPlan;
      }

      const properties = {};
      for (const [key, value] of formData.entries()) {
        if (!key.startsWith("properties[") || !key.endsWith("]")) continue;

        const propertyName = key.slice("properties[".length, -1);
        if (!propertyName) continue;
        properties[propertyName] = String(value);
      }

      if (Object.keys(properties).length > 0) {
        primaryItem.properties = properties;
      }

      const items = [
        primaryItem,
        ...checkedAccessoryInputs
          .map((input) => input.value)
          .filter(Boolean)
          .map((id) => ({
            id,
            quantity: 1,
          })),
      ];

      Cart.addItems(this.id, items, addToCartOptions, cartCallbacks);
      return;
    }

    Cart.add(this.id, formData, cartCallbacks);
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #onVariantUpdateNextEventLoop = (event) => {
    setTimeout(() => this.#onVariantUpdate(event));
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector(
      '[ref="addToCartButton"]',
    );

    if (!currentAddToCartButton) return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        "hidden",
        "true",
      );
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? "";

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100",
        );
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("add-to-cart-form-component")) {
  customElements.define("add-to-cart-form-component", AddToCartFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = "1";

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(
        progress,
        startPoint,
        controlPoint1,
        controlPoint2,
        endPoint,
      );

      //Update the position of the flying thingy
      this.style.setProperty("--x", `${position.x}px`);
      this.style.setProperty("--y", `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty("--scale", `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = "0";
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty("--x", `${startPoint.x}px`);
    this.style.setProperty("--y", `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
